import { DOCUMENT, NgIf } from '@angular/common';
import { AfterViewInit, Component, Inject, Renderer2 } from '@angular/core';
import { NavigationEnd, Route, Router, RouterOutlet } from '@angular/router';
import { AltNavComponent } from '@compounds/alt-nav/1.0';
import { AppHeaderComponent } from '@compounds/app-header/1.0';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '@services/app-config/1.0/app-config.service';
import { InactivityService } from '@services/inactivity/1.0/inactivity.service';
import { permission, PermissionsService } from '@services/permissions/1.0/permissions.service';
import { filter, first } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { TRANSLATIONS } from '@assets/i18n';
import { BrandService } from '@services/brand/1.0/brand.service';
import { CallCenterModeService } from '@services/call-center/call-center-mode.service';
import { ResellerModeService } from '@services/reseller/reseller-mode.service';

@Component({
  selector: 'txp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: { class: 'txp-root' },
  standalone: true,
  imports: [NgIf, RouterOutlet, AppHeaderComponent, AltNavComponent],
})
export class AppComponent implements AfterViewInit {
  title: string;
  currentRoute: string;
  logoutUri: string;
  isHomePage: boolean;
  navConfig: LocalRoute[] = [];
  isCallCenterModeActive = false;
  isResellerModeActive = false;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _: PermissionsService,
    private appConfig: AppConfigService,
    private brandService: BrandService,
    private inactivityService: InactivityService,
    private renderer2: Renderer2,
    private router: Router,
    private translateService: TranslateService,
    private callCenterModeService: CallCenterModeService,
    private resellerModeService: ResellerModeService,
  ) {
    const languageCodes = Object.keys(TRANSLATIONS);
    const selectedLanguage = localStorage.getItem('defaultLanguage');
    const defaultLanguage = languageCodes.includes(selectedLanguage)
      ? selectedLanguage
      : appConfig.settings.defaultLanguage;

    this.inactivityService.init();
    this.title = this.appConfig.settings.tenant;
    this.translateService.setDefaultLang(defaultLanguage);
    this.translateService
      .get('app.nav')
      .pipe(first())
      .subscribe((translations) => {
        const environmentKey = extractHostEnv().key;
        const hostEnvironment = extractHostEnv().env;
        const externalUrlForEnvironment = EXTERNAL_ROUTES[environmentKey || environment.name];
        const brandMessage = translations.pleaseSelectABrand;
        this.navConfig = [
          { icon: 'tce-icon-brand', path: '/brand', name: translations.brand },
          {
            icon: 'tce-icon-stores',
            path: '/store',
            name: translations.stores,
            permission: 'store:read',
          },
          {
            icon: 'tce-icon-details',
            path: '/boss',
            name: translations.boss,
            hide: true,
            permission: 'boss.entity:read',
          },
          {
            icon: 'tce-icon-orders',
            path: '/orders',
            name: translations.orders,
            permission: 'order:read',
          },
          {
            icon: 'tce-icon-catalog',
            path: '/catalog',
            name: translations.catalog,
            permission: 'catalog:write',
          },
          {
            icon: 'tce-icon-menu',
            path: '/menu',
            name: translations.menu,
            permission: 'menu:read',
          },
          { icon: 'tce-icon-user-tie', path: '/customers', name: translations.customers },
          {
            icon: 'tce-icon-coupons',
            path: '/coupons',
            name: translations.coupons,
            hide: true,
            permission: 'coupon-management:read',
          },
          {
            icon: 'tce-icon-coupons',
            path: '/loyalty',
            name: translations.loyalty,
            hide: true,
            permission: 'coupon-management:read',
          },
          {
            icon: 'tce-icon-users',
            path: '/users',
            name: translations.users,
            permission: 'user:read',
          },
          {
            icon: 'tce-icon-report',
            path: '/report',
            name: translations.report,
            permission: 'report:read',
          },
        ];

        // if (!environment.production && !!externalUrlForEnvironment) {
        //   this.navConfig.push({
        //     icon: 'tce-icon-xp',
        //     path: externalUrlForEnvironment,
        //     name: translations.txp,
        //     permission: 'txp-configs:read',
        //     absolute: hostEnvironment === 'stage',
        //     message: brandMessage,
        //   });
        // }

        this.navConfig.push({
          icon: 'tce-icon-phone',
          path: '/call-center/manage',
          permission: 'call-centers:write',
          hide: true,
          name: translations.callCenter,
        });
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isHomePage = event.url === '/' || event.url.startsWith('/auth-error');
        this.currentRoute = event.url;
      });

    this.callCenterModeService.getIsActive().subscribe((isActive) => {
      this.isCallCenterModeActive = isActive;
    });

    this.resellerModeService.getIsActive().subscribe((isActive) => {
      this.isResellerModeActive = isActive;
    });
  }

  ngAfterViewInit() {
    this.setGTagManager();
    this.brandService.currentBrandBehaviorSubject
      .pipe(filter((brand) => brand?.isSet))
      .subscribe((brand) => {
        if (brand.configurations?.settings?.bossPosEnable) {
          this.navConfig.forEach((nav) => {
            if (nav.icon === 'tce-icon-details') {
              nav.hide = false;
            }
          });
        }

        if (brand.configurations?.products?.coupons) {
          this.navConfig.forEach((nav) => {
            if (nav.path === '/coupons') {
              nav.hide = false;
              return;
            }
          });
        }

        if (brand.configurations?.products?.loyalty) {
          this.navConfig.forEach((nav) => {
            if (nav.path === '/loyalty') {
              nav.hide = false;
              return;
            }
          });
        }

        if (
          brand.aggregatorConfigurations?.['consumer-api']?.orderingChannels?.['call_center']
            ?.enabled
        ) {
          this.navConfig.forEach((nav) => {
            if (nav.path === '/call-center/manage') {
              nav.hide = false;
              return;
            }
          });
        }
      });
  }

  private setGTagManager() {
    const GTM_ID = this.appConfig.settings.analytics.gtmId;
    const GTM_SCRIPT_ELEM = this.document.getElementById('data-gtm');
    if (GTM_ID && !GTM_SCRIPT_ELEM) {
      const head = this.document.getElementsByTagName('head')[0];
      const script = this.renderer2.createElement('script');
      script.id = 'data-gtm';
      script.type = 'text/javascript';
      script.innerHTML =
        '(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start":' +
        'new Date().getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],' +
        'j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src=' +
        '"https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);' +
        '})(window,document,"script","dataLayer","' +
        GTM_ID +
        '");';
      head.appendChild(script);
    }
  }
}

interface LocalRoute extends Route {
  icon?: string;
  name?: string;
  hide?: boolean;
  permission?: 'SuperAdmin' | keyof typeof permission;
  absolute?: boolean;
  message?: string;
}

enum EXTERNAL_ROUTES {
  development = 'https://admin.dev.tce.tillster.com/',
  qa = 'https://admin.qa.tce.tillster.com/',
  stage = 'https://admin.staging.tce.tillster.com/',
}

function extractHostEnv(): { key: string; env: string } {
  enum ENVIRONMENT_MAP {
    'dev-a' = 'development',
    'dev-b' = 'development',
    'dev-c' = 'development',
    stage = 'development',
  }
  const hostList = window.location.host.split('.')[0].split('-');
  const env: string = hostList.length > 2 ? `${hostList[0]}-${hostList[1]}` : hostList[0];
  return {
    key: ENVIRONMENT_MAP[env],
    env: env,
  };
}
